import { IAuthRepository } from './IAuthRepository';
import { AuthApiRepository } from './AuthApiRepository';
import { API_BASE_URL } from '../../../config/api';

/**
 * Auth Repository 팩토리
 * 환경에 따라 적절한 Repository 구현체를 제공
 */
export class AuthRepositoryFactory {
  private static instance: IAuthRepository | null = null;

  /**
   * Repository 인스턴스 생성
   */
  static createRepository(config?: {
    type?: 'api' | 'mock' | 'legacy';
    baseURL?: string;
    apiKey?: string;
  }): IAuthRepository {
    const { type = 'api', baseURL, apiKey } = config || {};

    switch (type) {
      case 'api':
        return new AuthApiRepository(
          baseURL || process.env.REACT_APP_API_BASE_URL || API_BASE_URL,
          apiKey || process.env.REACT_APP_API_KEY
        );

      case 'mock':
        // 개발/테스트용 Mock Repository
        return this.createMockRepository();

      case 'legacy':
        // Legacy 모드는 API 모드로 폴백
        return new AuthApiRepository(
          baseURL || process.env.REACT_APP_API_BASE_URL || API_BASE_URL,
          apiKey || process.env.REACT_APP_API_KEY
        );

      default:
        throw new Error(`지원하지 않는 Repository 타입: ${type}`);
    }
  }

  /**
   * 싱글톤 Repository 인스턴스 반환
   */
  static getInstance(config?: {
    type?: 'api' | 'mock' | 'legacy';
    baseURL?: string;
    apiKey?: string;
  }): IAuthRepository {
    if (!this.instance) {
      this.instance = this.createRepository(config);
    }
    return this.instance;
  }

  /**
   * Repository 인스턴스 재설정
   */
  static resetInstance(): void {
    this.instance = null;
  }

  /**
   * Mock Repository 생성 (테스트용)
   */
  private static createMockRepository(): IAuthRepository {
    // 간단한 Mock 구현체 반환
    return {
      // User 관련 메서드
      findUserByEmail: async () => null,
      findUserById: async () => null,
      createUser: async (user) => user,
      updateUser: async (user) => user,
      deleteUser: async () => {},
      findUsers: async () => ({ users: [], total: 0, page: 1, limit: 10 }),

      // Session 관련 메서드
      findSessionById: async () => null,
      findActiveSessionsByUserId: async () => [],
      createSession: async (session) => session,
      updateSession: async (session) => session,
      deleteSession: async () => {},
      cleanupExpiredSessions: async () => 0,

      // 인증 관련 메서드
      authenticate: async () => null,
      findUserByToken: async () => null,
      refreshToken: async () => null,
      logout: async () => {},
      logoutAllSessions: async () => {},

      // 로그인 시도 관련 메서드
      recordLoginAttempt: async () => {},
      findLoginAttemptsByEmail: async () => [],
      findLoginAttemptsByIP: async () => [],
      cleanupOldLoginAttempts: async () => 0,

      // 비밀번호 관련 메서드
      changePassword: async () => {},
      createPasswordResetToken: async () => null,
      validatePasswordResetToken: async () => null,
      resetPassword: async () => false,

      // 보안 관련 메서드
      lockAccount: async () => {},
      unlockAccount: async () => {},
      setAccountActive: async () => {},

      // 감사 로그 관련 메서드
      recordSecurityEvent: async () => {},
      findSecurityEvents: async () => [],

      // 캐시 관련 메서드
      invalidateUserCache: async () => {},
      invalidateSessionCache: async () => {},
      invalidateAllAuthCache: async () => {},
    };
  }

  /**
   * 환경별 기본 설정
   */
  static getDefaultConfig(): {
    type: 'api' | 'mock' | 'legacy';
    baseURL?: string;
    apiKey?: string;
  } {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isTest = process.env.NODE_ENV === 'test';
    const useLegacy = process.env.REACT_APP_USE_LEGACY_AUTH === 'true';

    if (isTest) {
      return { type: 'mock' };
    }

    // 점진적 마이그레이션을 위한 Legacy 모드 지원
    if (useLegacy) {
      return { type: 'legacy' };
    }

    return {
      type: 'api',
      baseURL: process.env.REACT_APP_API_BASE_URL || API_BASE_URL,
      apiKey: process.env.REACT_APP_API_KEY,
    };
  }
}